import { Box, FormControl, ListSubheader, MenuItem, Select } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { BUILDING_SECTIONS } from "./buildingSections";

export const ALL_PRODUCTS_VALUE = "all";

// Reusable Products dropdown for Building Circularity tables (Material Composition,
// Utility). Lists the same WBLCA Building Sections shown in the Workspace's WBLCA tab
// (BuildingSectionsAccordion, via the shared BUILDING_SECTIONS constant), grouped by
// category. Purely presentational — callers own the fetching of building processes and
// the filtering logic; this component just renders the selector and reports the chosen
// subcategory id (or ALL_PRODUCTS_VALUE) via onChange.
const BuildingProductDropdown = ({ value, onChange, sx, showAllProductsOption = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Only wired up when showAllProductsOption is false (MCI Calculation/Design Assessment):
  // "All Products" isn't a valid choice there, but the shared selection can still be
  // ALL_PRODUCTS_VALUE if it was last set from a tab that does allow it — MUI Select can't
  // find a matching MenuItem to render in that case and shows an unlabelled blank, so this
  // renders a placeholder instead. Left undefined for every other caller so their default
  // MUI rendering (full MenuItem content, incl. the code badge) is completely unchanged.
  const placeholderRenderValue = showAllProductsOption ? undefined : (selected) => {
    if (selected === ALL_PRODUCTS_VALUE) {
      return <Box component="span" sx={{ color: theme.palette.text.secondary }}>{t("Select a product")}</Box>;
    }
    const sub = BUILDING_SECTIONS.flatMap((section) => section.subcategories).find((s) => s.id === selected);
    return sub
      ? <>{t(sub.name)} <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: theme.palette.text.secondary }}>{sub.code}</Box></>
      : selected;
  };

  return (
    <FormControl size="small" sx={{ minWidth: 220, ...sx }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        renderValue={placeholderRenderValue}
        sx={{
          fontFamily: "Poppins",
          fontSize: 14,
          height: 40,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981", borderWidth: 2 },
        }}
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
      >
        {showAllProductsOption && (
          <MenuItem value={ALL_PRODUCTS_VALUE} sx={{ fontFamily: "Poppins", fontSize: 14 }}>
            {t("All Products")}
          </MenuItem>
        )}
        {BUILDING_SECTIONS.flatMap((section) => [
          <ListSubheader key={`hdr-${section.id}`} sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, lineHeight: "32px", color: theme.palette.text.secondary }}>
            {t(section.name)}
          </ListSubheader>,
          ...section.subcategories.map((sub) => (
            <MenuItem key={sub.id} value={sub.id} sx={{ fontFamily: "Poppins", fontSize: 14, pl: 3 }}>
              {t(sub.name)} <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: theme.palette.text.secondary }}>{sub.code}</Box>
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
};
export default BuildingProductDropdown;
